import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping';
import { PdpSharedBlurbDesignYourOwn } from '../PdpSharedBlurbs/PdpSharedBlurbDesignYourOwn';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime';
import { PdpSharedBlurbRareFind } from '../PdpSharedBlurbs/PdpSharedBlurbRareFind';
import { useRenderBlurbs } from './hooks/useRenderBlurbs';
import { trackSiderailRedesignVariants } from '../helpers/siderailVariantHelpers';
import { RelayVariables, Store } from '../../global/relayVariableTypes';

import { PdpRespSidebarBlurbs_viewer$data } from './__generated__/PdpRespSidebarBlurbs_viewer.graphql';
import { PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql';
import { PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql';
import { PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql';
import { PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

type Props = {
    viewer: PdpRespSidebarBlurbs_viewer$data;
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null;
    sku: PdpRespSidebarBlurbs_sku$data | null;
    currency?: string;
    itemQuantity?: number;
};

export const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    viewer,
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
}) => {
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );

    const { isSiderailRedesignVariant } = relayVariables;

    useEffect(() => {
        trackSiderailRedesignVariants();
    }, []);

    if (!renderBlurbs) {
        return null;
    }

    return (
        <div className={styles.container}>
            {!isSiderailRedesignVariant && <PdpSharedBlurbDesignYourOwn item={item} />}
            {!isSiderailRedesignVariant && <PdpSharedBlurbLeadTime sku={sku} item={item} />}
            {viewer.pdpRareFindTreatment && !isSiderailRedesignVariant && (
                <PdpSharedBlurbRareFind item={item} />
            )}
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                viewer={viewer}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.isClient}
                itemQuantity={itemQuantity}
            />
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            # TOF-171: Delete when cleaning up feature flag
            pdpRareFindTreatment: featureFlag(feature: "pdpRareFindTreatment")
            ...PdpRespSidebarBlurbShipping_viewer
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...useRenderBlurbs_item
            ...PdpSharedBlurbLeadTime_item @skip(if: $isSiderailRedesignVariant)
            ...PdpRespSidebarBlurbShipping_item
            ...PdpSharedBlurbDesignYourOwn_item @skip(if: $isSiderailRedesignVariant)
            ...PdpSharedBlurbRareFind_item @skip(if: $isSiderailRedesignVariant)
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            serviceId
            ...PdpRespSidebarBlurbShipping_user
            ...PdpRespSidebarBlurbShipping_user
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ...PdpRespSidebarBlurbShipping_sku @include(if: $isMultiSku)
            ...PdpSharedBlurbLeadTime_sku @skip(if: $isSiderailRedesignVariant)
        }
    `,
});
